import React, { useState, useEffect } from 'react';
import { Search, User, Award, FileText } from 'lucide-react';
import api from '../api';
import Pagination from '../common/Pagination';
import EmptyState from '../common/EmptyState';

const NotesTab = () => {
  const [notes, setNotes] = useState([]);
  const [epreuves, setEpreuves] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedEpreuve, setSelectedEpreuve] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState(null);

  // Charger les sessions disponibles
  const fetchSessions = async () => {
    try {
      const response = await api.get('/concours/sessions/');
      if (response.data) {
        const sessionData = response.data.data || response.data.results || [];
        setSessions(Array.isArray(sessionData) ? sessionData : []);
        // Sélectionner la première session par défaut
        if (sessionData.length > 0) {
          setSelectedSession(sessionData[0].id);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error);
    }
  };

  // Charger les épreuves pour une session
  const fetchEpreuves = async (sessionId) => {
    if (!sessionId) return;
    
    try {
      const response = await api.get(`/concours/sessions/${sessionId}/epreuves/`);
      if (response.data) {
        const epreuveData = response.data.data || response.data.results || [];
        setEpreuves(Array.isArray(epreuveData) ? epreuveData : []);
        // Sélectionner la première épreuve par défaut
        if (epreuveData.length > 0) {
          setSelectedEpreuve(epreuveData[0].id);
        } else {
          setSelectedEpreuve('');
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des épreuves:', error);
      setEpreuves([]);
      setSelectedEpreuve('');
    }
  };

  const fetchNotes = async () => {
    if (!selectedEpreuve) return;
    
    setLoading(true);
    setError('');
    
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        page_size: pageSize
      });
      
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      const response = await api.get(`/concours/epreuves/${selectedEpreuve}/notes/?${queryParams}`);
      
      if (response.data) {
        const responseData = response.data.data || response.data.results || [];
        setNotes(Array.isArray(responseData) ? responseData : []);
        setPagination(response.data.pagination || {
          count: response.data.count || 0,
          total_pages: Math.ceil((response.data.count || 0) / pageSize),
          current_page: currentPage,
          page_size: pageSize
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des notes');
      setNotes([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  // Charger les sessions au montage
  useEffect(() => {
    fetchSessions();
  }, []);

  // Charger les épreuves quand la session change
  useEffect(() => {
    if (selectedSession) {
      fetchEpreuves(selectedSession);
    }
  }, [selectedSession]);

  // Charger les notes quand l'épreuve ou les paramètres changent
  useEffect(() => {
    if (selectedEpreuve) {
      const timeoutId = setTimeout(() => {
        fetchNotes();
      }, searchTerm ? 300 : 0);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedEpreuve, currentPage, pageSize, searchTerm]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSessionChange = (e) => {
    setSelectedSession(e.target.value);
    setCurrentPage(1);
  };

  const handleEpreuveChange = (e) => {
    setSelectedEpreuve(e.target.value);
    setCurrentPage(1);
  };

  const getGradeColor = (note) => {
    if (note >= 16) return 'text-green-600';
    if (note >= 12) return 'text-blue-600';
    if (note >= 10) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Fonction pour formater l'affichage de l'épreuve
  const formatEpreuveDisplay = (epreuve) => {
    const matiereName = epreuve.matiere?.nom || epreuve.matiere_nom || 'Matière';
    const dateDisplay = epreuve.date_epreuve || 
                       (epreuve.date_heure ? new Date(epreuve.date_heure).toLocaleDateString('fr-FR') : 'Date non définie');
    return `${matiereName} - ${dateDisplay}`;
  };

  if (loading && notes.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex gap-4 flex-1">
          {/* Sélecteur de session */}
          <div className="min-w-48">
            <select
              value={selectedSession}
              onChange={handleSessionChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Sélectionner une session</option>
              {sessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.nom}
                </option>
              ))}
            </select>
          </div>

          {/* Sélecteur d'épreuve */}
          <div className="min-w-48">
            <select
              value={selectedEpreuve}
              onChange={handleEpreuveChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!selectedSession}
            >
              <option value="">Sélectionner une épreuve</option>
              {epreuves.map((epreuve) => (
                <option key={epreuve.id} value={epreuve.id}>
                  {formatEpreuveDisplay(epreuve)}
                </option>
              ))}
            </select>
          </div>
          
          {/* Barre de recherche */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Rechercher une note..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {!selectedSession ? (
        <EmptyState
          icon={Award}
          title="Sélectionner une session"
          description="Veuillez sélectionner une session pour voir les épreuves."
        />
      ) : !selectedEpreuve ? (
        <EmptyState
          icon={FileText}
          title="Sélectionner une épreuve"
          description="Veuillez sélectionner une épreuve pour voir les notes."
        />
      ) : notes.length === 0 && !loading ? (
        <EmptyState
          icon={Award}
          title="Aucune note"
          description="Aucune note trouvée pour cette épreuve."
        />
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidat
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Note
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Correcteur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date correction
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notes.map((note) => (
                  <tr key={note.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {note.candidat?.nom_complet || `${note.candidat?.nom} ${note.candidat?.prenom}` || 'Candidat inconnu'}
                          </div>
                          {note.candidat?.numero_candidat && (
                            <div className="text-sm text-gray-500">N° {note.candidat.numero_candidat}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`flex items-center text-sm font-medium ${getGradeColor(note.note)}`}>
                        <Award className="h-4 w-4 mr-1" />
                        {note.note}/20
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {note.correcteur?.nom_complet || note.correcteur || 'Non assigné'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {note.date_correction ? new Date(note.date_correction).toLocaleDateString('fr-FR') : 'Non corrigé'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.total_pages}
                pageSize={pageSize}
                totalCount={pagination.count}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotesTab; 